<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useId, useTemplateRef } from 'vue'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import type { IConsentsData, IFormSectionProps, ISubjectInput } from '../ts/types'
import FormDataCreator from '../FormDataCreator'
import { ESelectorItemVariants } from '@/ui/plushComponents/VSelector/types'
import useBreakpoint from '@/ui/composables/useBreakpoint'
import { useDrawer } from '@/cms-ui/composables/useDrawer'
import { EDrawerType } from '@/cms-ui/components/Drawer/types'
import type { DrawerData } from '@/cms-ui/ts/interfaces/IDrawer'
import type { TBaseInputStates } from '@/ui/plushComponents/VInputField/types'

// Component props
const props = withDefaults(defineProps<IFormSectionProps>(), {
    drawerType: false,
})

// Composable hooks
const { invokeDrawer } = useDrawer()
const { isMobile, isDesktop } = useBreakpoint()
const subjectsRef = useTemplateRef('subjectsRef')

// Reactive state
const contentHeight = ref<string>()
const showReadMore = ref(false)

// Refs for tracking validation states
const touchedFields = ref<Record<string, boolean>>({})
const isTyping = ref<Record<string, boolean>>({})

// Generate a unique formId
const formId = `form-${useId()}`

// Initialize form data manager
const formDataCreator = new FormDataCreator({
    subjects: props.subjects || [],
    consents: props.consents || [],
    consentsValidation: props.consentsValidation || {},
    formId,
})

// Extract form data and state
const { inputsData, selectorValue, formState } = formDataCreator.getFormData()
const { errors, isFormSent, isFormSuccess, formSubmitted, isSubmitting } = formState

// VSelector subject options
const options = computed(
    () =>
        props.subjects?.map((subject, index) => ({
            id: `form-lead-subject-${index}`,
            label: subject.title,
            value: subject.title,
        })) || [],
)

// Drawer configuration
const drawerLegalContent: DrawerData = {
    drawerTypeMobile: EDrawerType.DYNAMIC,
    drawerTitle: props.legalTitle,
    content: [
        {
            type: 'DrawerTextBlock',
            data: {
                paragraph: props.legalDescription,
            },
        },
    ],
}

/**
 * Updates consent data in the form data manager
 */
function handleConsentsDataUpdate(data: IConsentsData, name: string, parentName?: string): void {
    formDataCreator.setConsentsData(data)
    formDataCreator.validateConsentOnChange(name, parentName)
}

/**
 * Handles input blur event
 */
async function handleInputBlur(subjectTitle: string, inputLabel: string, index: number): Promise<void> {
    const inputId = `phone-input-${subjectTitle}-${index}`
    touchedFields.value[inputId] = true
    isTyping.value[inputId] = false
    await formDataCreator.validateField(subjectTitle, inputLabel, index)
    setHeight()
}

/**
 * Handles input change event
 */
async function handleInputChange(subjectTitle: string, inputLabel: string, index: number): Promise<void> {
    const inputId = `phone-input-${subjectTitle}-${index}`
    isTyping.value[inputId] = true
    await formDataCreator.validateField(subjectTitle, inputLabel, index)
    setHeight()
}

/**
 * Sets the form height based on the active subject
 */
function setHeight(): void {
    const el = subjectsRef.value?.find(el => el.id === selectorValue.value)
    contentHeight.value = `${el?.offsetHeight}px`
}

const handleResize = useDebounceFn(setHeight, 100)

/**
 * Determines the input state based on validation and active state
 */
function getInputState(input: ISubjectInput, index: number, isActive: boolean, subjectTitle: string): TBaseInputStates {
    const errorKey = `phone-input-${subjectTitle}-${index}`

    if (formSubmitted.value && errors.value[errorKey]) {
        return 'error'
    }

    if (props.subjects?.length === 1) {
        return 'default'
    }

    return isActive ? 'default' : 'disabled'
}

/**
 * Gets error message for input field
 */
function getInputErrorMessage(input: ISubjectInput, index: number, subjectTitle: string): string {
    const errorKey = `phone-input-${subjectTitle}-${index}`

    // Return validation error if present
    if (formSubmitted.value && errors.value[errorKey]) {
        return errors.value[errorKey]
    }

    // Return required validation message if specified
    if (input.validation?.['phone.required']) {
        return input.validation['phone.required']
    }

    return ''
}

/**
 * Handles form submission
 */
async function handleSubmit(): Promise<void> {
    const success = await formDataCreator.submitForm()

    // If validation failed, scroll to first error
    if (!success) {
        await nextTick(() => {
            const firstErrorId = Object.keys(errors.value)[0]
            if (firstErrorId) {
                const errorElement = document.getElementById(firstErrorId)
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                }
            }
            setHeight()
        })
    }
}

/**
 * Resets form to initial state for retry
 */
function handleRetry(): void {
    formDataCreator.handleRetry()
}

// Event listeners and lifecycle hooks
useEventListener(window, 'resize', handleResize)
onMounted(() => useDebounceFn(setHeight, 0))
</script>

<template>
    <div class="relative">
        <Transition name="fade">
            <LeadFormPartsFormSent
                v-show="isFormSent"
                class="bg-neutral-primary -mx-sm px-md md:px-sm absolute inset-0 z-10"
                :is-success="isFormSuccess"
                :title="isFormSuccess ? successContent.title : errorContent.title"
                :description="isFormSuccess ? successContent.description : errorContent.description"
                :drawer-type="drawerType"
                @retry="handleRetry"
            />
        </Transition>

        <div
            v-if="drawerType"
            class="mb-md lg:mb-lg"
        >
            <div class="flex">
                <div class="flex grow items-center lg:flex-col lg:items-start lg:justify-center">
                    <h3 class="heading-sm lg:heading-md text-neutral-primary line-clamp-3">
                        {{ heading }}
                    </h3>

                    <div
                        v-html-gpp="paragraph"
                        class="paragraph-md lg:paragraph-lg text-neutral-primary hidden lg:block"
                    />
                </div>

                <div class="pl-base lg:pl-lg">
                    <BaseImage
                        v-if="responsiveImage"
                        picture-classes=""
                        :image-data="responsiveImage"
                        :alt="imageAlt"
                        class="size-[96px] lg:size-[144px]"
                    />
                </div>
            </div>

            <div
                v-html-gpp="paragraph"
                class="paragraph-md lg:paragraph-lg text-neutral-primary mt-base lg:hidden"
            />
        </div>

        <h4
            v-if="title"
            class="heading-xs lg:heading-sm text-neutral-primary mb-md lg:mb-lg line-clamp-3"
        >
            {{ title }}
        </h4>
        <h5
            v-if="subjectsGroupTitle"
            class="label-md text-comp-high-contrast"
        >
            {{ subjectsGroupTitle }}
        </h5>
        <form
            id="lead-form"
            class="flex flex-col"
            :class="{ 'mt-sm': title || subjectsGroupTitle }"
            novalidate
            @submit.prevent="handleSubmit"
        >
            <div class="max-md:-mx-sm">
                <VSelector
                    v-if="subjects?.length > 1"
                    v-model="selectorValue"
                    class="mb-md max-md:px-sm w-full"
                    :default-value="selectorValue"
                    :options="options"
                    :variant="ESelectorItemVariants.SIMPLE"
                    :layout="isMobile && subjects?.length > 2 ? 'swipe' : 'grid'"
                    @click="setHeight"
                />
            </div>

            <div
                class="transition-height px-2xs -mx-2xs overflow-hidden duration-500 ease-in-out"
                :style="{ height: contentHeight }"
            >
                <div
                    v-for="subject in subjects"
                    v-show="selectorValue === subject.title || subjects?.length === 1"
                    :id="subject.title"
                    :key="subject.title"
                    ref="subjectsRef"
                >
                    <div
                        v-for="(input, i) in subject.inputs"
                        :key="input.label"
                        :class="{
                            'mt-base': i > 0,
                            'pb-2xs': i === subject.inputs.length - 1,
                            'lg:max-w-[360px]': drawerType,
                        }"
                    >
                        <VInputField
                            v-model="inputsData[subject.title][input.label]"
                            :variant="input.type"
                            data-test="input-field"
                            :options="{
                                id: `phone-input-${subject.title}-${i}`,
                                name: `phone-input-${subject.title}-${i}`,
                                state: getInputState(input, i, selectorValue === subject.title, subject.title),
                                label: input.label,
                                errorMessage: getInputErrorMessage(input, i, subject.title),
                                helper: input.helperText,
                                required: true,
                            }"
                            @blur="handleInputBlur(subject.title, input.label, i)"
                            @update:model-value="handleInputChange(subject.title, input.label, i)"
                        />
                    </div>
                </div>
            </div>

            <div v-if="consents">
                <LeadFormPartsConsents
                    :form-id="formId"
                    :title="consentsTitle"
                    :consents="consents"
                    :validation="consentsValidation"
                    :errors="errors"
                    :show-errors="formSubmitted"
                    :labels="labels"
                    @update:data="handleConsentsDataUpdate"
                />
            </div>

            <template v-if="consents?.length > 1">
                <p
                    v-for="(label, i) in labels"
                    :key="`label-${i}`"
                    class="paragraph-sm text-neutral-tertiary"
                    :class="{ 'mt-md lg:mt-lg order-last': drawerType }"
                >
                    {{ label }}
                </p>
            </template>
            <p
                class="paragraph-sm text-neutral-tertiary mt-base"
                :class="{ 'order-last': drawerType }"
            >
                {{ legalReadmore }}
            </p>

            <VLink
                v-if="legalReadmore && !drawerType"
                class="mt-2xs"
                size="SMALL"
                data-test="lead-link-read-more"
                @click.prevent.stop="invokeDrawer(drawerLegalContent)"
            >
                Czytaj więcej
            </VLink>

            <BaseButton
                :class="drawerType ? 'self-start' : 'w-full'"
                class="mt-md w-full lg:w-auto"
                variant="primary"
                :size="isDesktop ? 'LARGE' : 'MEDIUM'"
                data-test="lead-form-submit"
                :loading="isSubmitting"
                type="submit"
                :state="isSubmitting ? 'loading' : 'default'"
            >
                {{ actionTitle }}
            </BaseButton>
        </form>

        <div
            v-if="drawerType"
            class="mt-2xs order-last"
        >
            <VTransition v-model="showReadMore">
                <div
                    v-html-gpp="legalDescription"
                    class="pb-lg pt-md"
                />
            </VTransition>

            <VLink
                size="SMALL"
                class="text-comp-high-contrast"
                data-test="lead-link-read-more"
                :trailing-icon="showReadMore ? 'chevron-up' : 'chevron-down'"
                @click.prevent="showReadMore = !showReadMore"
            >
                {{ showReadMore ? 'Czytaj mniej' : 'Czytaj więcej' }}
            </VLink>
        </div>
    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
